/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ErrorTypes } from "../../model/error";
import { Locale, LocaleKeys } from "../../model/locale";
import { Log, OperationalLogger } from "../../model/logging";
import { showUnityDialog } from "../unityDialog";
import type { UnityDialogConfig } from "../unityDialog";
import type { UnityAppDispatch } from "../UnityAppStore";
import type { TProfileError } from "./profiles.types";

export const PROFILE_ERROR_OPERATIONS = {
  CREATE: "create",
  DELETE: "delete",
  LOAD: "load",
  LOAD_PARTNER_CONFIG: "loadPartnerConfig",
  SELECT: "select",
  UPDATE: "update"
} as const;

export type ProfileErrorOperation =
  (typeof PROFILE_ERROR_OPERATIONS)[keyof typeof PROFILE_ERROR_OPERATIONS];

export interface TProfileErrorDialogOptions {
  error: TProfileError;
  onDismiss?: () => void;
  onRetry: () => void;
  operation: ProfileErrorOperation;
}

export interface TProfileRequestOptions<T> {
  dispatch: UnityAppDispatch;
  methodName: string;
  onDismiss?: () => void;
  onError?: (error: unknown) => boolean;
  onSuccess?: (value: T) => void | Promise<void>;
  operation: ProfileErrorOperation;
  request: () => Promise<T>;
  tag: string;
}

const NON_RETRYABLE_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  ErrorTypes.PROFILE_BAD_REQUEST_BODY_A821,
  ErrorTypes.PROFILE_BAD_REQUEST_PARAMETERS_A822,
  ErrorTypes.PROFILE_RESOURCE_NOT_FOUND_A823,
  ErrorTypes.PROFILE_CONFLICT_A824,
  ErrorTypes.PROFILE_QUOTA_EXCEEDED_A825,
  ErrorTypes.PROFILE_CLIENT_BAD_REQUEST_A831,
  ErrorTypes.PROFILE_CLIENT_BAD_RESPONSE_A832,
  ErrorTypes.PROFILE_AUTHENTICATION_REQUIRED_A836,
  ErrorTypes.PROFILE_ACCESS_DENIED_A837
]);

function resolveCodeMessageKey(code: string): string | null {
  switch (code) {
    case ErrorTypes.PROFILE_TIMEOUT_A828:
    case ErrorTypes.PROFILE_CONNECTION_REFUSED_A829:
    case ErrorTypes.PROFILE_CONNECTION_FAILED_A830:
      return LocaleKeys.PROFILE_ERROR_NETWORK_MESSAGE;
    case ErrorTypes.PROFILE_RESOURCE_NOT_FOUND_A823:
      return LocaleKeys.PROFILE_ERROR_NOT_FOUND_MESSAGE;
    case ErrorTypes.PROFILE_QUOTA_EXCEEDED_A825:
      return LocaleKeys.PROFILE_ERROR_LIMIT_REACHED_MESSAGE;
    case ErrorTypes.PROFILE_AUTHENTICATION_REQUIRED_A836:
      return LocaleKeys.PROFILE_ERROR_AUTHENTICATION_MESSAGE;
    case ErrorTypes.PROFILE_ACCESS_DENIED_A837:
      return LocaleKeys.PROFILE_ERROR_ACCESS_DENIED_MESSAGE;
    default:
      return null;
  }
}

function resolveOperationMessageKey(operation: ProfileErrorOperation): string {
  switch (operation) {
    case PROFILE_ERROR_OPERATIONS.CREATE:
    case PROFILE_ERROR_OPERATIONS.UPDATE:
      return LocaleKeys.PROFILE_ERROR_SAVE_MESSAGE;
    case PROFILE_ERROR_OPERATIONS.DELETE:
      return LocaleKeys.PROFILE_ERROR_DELETE_MESSAGE;
    case PROFILE_ERROR_OPERATIONS.SELECT:
      return LocaleKeys.PROFILE_ERROR_SELECT_MESSAGE;
    case PROFILE_ERROR_OPERATIONS.LOAD:
    case PROFILE_ERROR_OPERATIONS.LOAD_PARTNER_CONFIG:
      return LocaleKeys.PROFILE_ERROR_SERVICE_MESSAGE;
  }
}

export function normalizeProfileError(
  error: unknown,
  fallbackMessage: string
): TProfileError | string {
  if (error !== null && typeof error === "object") {
    const code: unknown = "code" in error ? error.code : undefined;
    const message: unknown = "message" in error ? error.message : undefined;

    if (typeof code === "string" && typeof message === "string") {
      return { code: code, message: message };
    }
    if (typeof message === "string") {
      return message;
    }
  }

  return error instanceof Error ? error.message : fallbackMessage;
}

export function isProfileError(error: unknown): error is TProfileError {
  return (
    error !== null &&
    typeof error === "object" &&
    "code" in error &&
    typeof error.code === "string" &&
    "message" in error &&
    typeof error.message === "string"
  );
}

export function isProfileNotFoundError(error: TProfileError): boolean {
  return error.code === ErrorTypes.PROFILE_RESOURCE_NOT_FOUND_A823;
}

export function buildProfileErrorDialog(
  options: TProfileErrorDialogOptions
): UnityDialogConfig {
  const codeMessageKey: string | null = resolveCodeMessageKey(
    options.error.code
  );
  const messageKey: string =
    codeMessageKey !== null
      ? codeMessageKey
      : resolveOperationMessageKey(options.operation);
  const dismissAction = {
    id: "profile-error-dismiss",
    label: Locale.getString(LocaleKeys.ACTION_DISMISS),
    onPress: options.onDismiss
  };
  const actions = NON_RETRYABLE_ERROR_CODES.has(options.error.code)
    ? [dismissAction]
    : [
        {
          id: "profile-error-retry",
          label: Locale.getString(LocaleKeys.ACTION_TRY_AGAIN),
          onPress: options.onRetry
        },
        dismissAction
      ];

  return {
    actions: actions,
    defaultFocusedActionId: "profile-error-dismiss",
    errorCode: options.error.code,
    message: Locale.getString(messageKey),
    title: Locale.getString(LocaleKeys.PROFILE_ERROR_TITLE),
    variant: "error"
  };
}

export function showProfileErrorDialog(
  options: TProfileErrorDialogOptions
): (dispatch: UnityAppDispatch) => void {
  return (dispatch: UnityAppDispatch): void => {
    OperationalLogger.logUiError({
      errorCode: options.error.code,
      screen: options.operation
    });
    dispatch(showUnityDialog(buildProfileErrorDialog(options)));
  };
}

export async function runProfileRequest<T>(
  options: TProfileRequestOptions<T>
): Promise<void> {
  const startMs: number = Date.now();
  try {
    const value: T = await options.request();
    if (options.onSuccess !== undefined) {
      await options.onSuccess(value);
    }
  } catch (error: unknown) {
    const latencyMs: number = Date.now() - startMs;
    Log.e(options.tag, options.methodName, error);
    OperationalLogger.logApiFailure(error, {
      errorCode: isProfileError(error) ? error.code : undefined,
      latencyMs: latencyMs
    });
    if (options.onError !== undefined && options.onError(error)) {
      return;
    }
    if (isProfileError(error)) {
      options.dispatch(
        showProfileErrorDialog({
          error: error,
          onDismiss: options.onDismiss,
          onRetry: (): void => {
            void runProfileRequest(options);
          },
          operation: options.operation
        })
      );
    } else {
      OperationalLogger.logError({ errorCode: "" });
    }
  }
}
